import { Color, SectionInterface } from "../types/store";
import { PartInterface } from "../../shared";
import { useStoreSystem } from "../useStoreSystem";
import { isNewSite } from "../../utils";
import { useShelvingStore } from "../useShelvingStore";

/**
 * Информация о штанге-части полки для цепочки продолжений
 */
interface ShelfRodInfo {
    itemId: string;
    partIndex: number;
    shouldUseAltId: boolean;
}

/**
 * Определяет логику продолжения для штанг в частях полок
 * Упрощенная логика: если слева на этой высоте нет полки со штангой -- это база, если есть -- продолжение
 */
export const getShelfRodChainIds = (sections: SectionInterface[]): ShelfRodInfo[] => {
    const results: ShelfRodInfo[] = [];

    // Собираем все полки со штангами-частями из всех секций
    const shelvesWithRods: Array<{
        item: any;
        sectionIndex: number;
        rodParts: Array<{ part: PartInterface; partIndex: number }>;
    }> = [];

    sections.forEach((section, sectionIndex) => {
        section.items.forEach((item) => {
            // Ищем штанги в частях полки
            const rodParts = item.parts
                .map((part, partIndex) => ({ part, partIndex }))
                .filter(({ part }) => part.type?.some((t) => t === "Штанга") && part.isVisible);

            if (rodParts.length > 0) {
                shelvesWithRods.push({
                    item,
                    sectionIndex,
                    rodParts,
                });
            }
        });
    });

    if (shelvesWithRods.length === 0) {
        return results;
    }

    // Для каждой полки со штангами проверяем, есть ли слева полка со штангой на той же высоте
    shelvesWithRods.forEach((currentShelf) => {
        const currentHeight = Math.round(currentShelf.item.position[1] * 1000); // Y позиция полки

        // Ищем полку слева (в предыдущих секциях) на той же высоте со штангой
        const hasLeftShelfWithRod = shelvesWithRods.some((leftShelf) => {
            const leftHeight = Math.round(leftShelf.item.position[1] * 1000);

            return (
                leftShelf.sectionIndex < currentShelf.sectionIndex && // слева
                leftHeight === currentHeight && // та же высота
                leftShelf.rodParts.length > 0
            ); // есть штанги
        });

        // Определяем тип штанг: база или продолжение
        const shouldUseAltId = hasLeftShelfWithRod;

        console.log(
            `Полка ${currentShelf.item.name} в секции ${currentShelf.sectionIndex} на высоте ${currentHeight}: ${
                shouldUseAltId ? "продолжение (есть полка слева со штангой)" : "база (нет полки слева со штангой)"
            }`
        );

        currentShelf.rodParts.forEach(({ part, partIndex }) => {
            results.push({
                itemId: currentShelf.item.id,
                partIndex,
                shouldUseAltId,
            });
        });
    });

    return results;
};

/**
 * Обновляет bitrixId для штанг в частях полок на основе логики продолжений
 */
export const updateShelfRodIds = (sections: SectionInterface[], color?: Color): SectionInterface[] => {
    const isNew = isNewSite();
    const colorBrackets = color || useShelvingStore.getState().colorsItems?.brackets;

    const rodChainInfo = getShelfRodChainIds(sections);
    const chainMap = new Map<string, boolean>();
    rodChainInfo.forEach((info) => chainMap.set(`${info.itemId}_${info.partIndex}`, info.shouldUseAltId));

    const itemsGroup = useStoreSystem.getState().itemsGroup;
    const allItems = itemsGroup.flatMap((group) => group.ELEMENTS || []);

    const pickRodNm = (originalItem?: any, useAlt?: boolean) => {
        const list = originalItem?.newNomenkl || [];
        if (!list.length) return undefined;
        const byColor = colorBrackets ? list.filter((n: any) => n.color === colorBrackets) : list;
        if (useAlt) {
            return byColor.find((n: any) => n?.nameNomenkl?.includes("Продление")) || byColor[0] || list[0];
        }
        return byColor.find((n: any) => !n?.nameNomenkl?.includes("Продление")) || byColor[0] || list[0];
    };

    return sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
            ...item,
            parts: item.parts.map((part, partIndex) => {
                const key = `${item.id}_${partIndex}`;
                if (!(part.type?.some((t) => t === "Штанга") && chainMap.has(key))) return part;

                const shouldUseAltId = chainMap.get(key);
                const baseId = part.bitrixIdOriginal || part.bitrixId;

                if (isNew) {
                    const originalItem = allItems.find(
                        (original) =>
                            original.BITRIX_OFFERS_IDS?.includes(part.bitrixId) ||
                            original.BITRIX_OFFER_ID === part.bitrixId
                    );

                    const nm = pickRodNm(originalItem, shouldUseAltId);
                    const nextBitrixId =
                        (nm?.bitrixId as string | undefined) || (shouldUseAltId && part.bitrixIdAlt) || baseId;
                    const nextPrice = (nm?.priceNomenkl as number | undefined) ?? part.price;
                    const nextName = nm?.nameNomenkl ?? part.name;

                    if (part.bitrixId !== nextBitrixId) {
                        console.log(
                            `Штанга-часть полки ${item.name} (partIndex: ${partIndex}): bitrixId ${part.bitrixId} → ${nextBitrixId} (isNew)`
                        );
                    }

                    return {
                        ...part,
                        bitrixId: nextBitrixId,
                        price: nextPrice,
                        name: nextName,
                    };
                }

                const originalItem = allItems.find(
                    (original) =>
                        original.BITRIX_OFFER_ID === (part.bitrixIdOriginal || part.bitrixId) ||
                        original.BITRIX_OFFER_ID_ALT === (part.bitrixIdOriginal || part.bitrixId)
                );

                const newBitrixId = shouldUseAltId && part.bitrixIdAlt ? part.bitrixIdAlt : baseId;
                if (part.bitrixId !== newBitrixId) {
                    console.log(
                        `Штанга-часть полки ${item.name} (partIndex: ${partIndex}): смена bitrixId с ${
                            part.bitrixId
                        } на ${newBitrixId}${shouldUseAltId ? " (альтернативный ID)" : " (основной ID)"}`
                    );
                }
                const updatedPart: typeof part = { ...part, bitrixId: newBitrixId };

                if (originalItem && (originalItem.nameNomenkl || originalItem.nameNomenklAlt)) {
                    const isUsingAltId = part.bitrixIdAlt && updatedPart.bitrixId === part.bitrixIdAlt;
                    const isUsingOriginalAltId =
                        !isUsingAltId &&
                        originalItem.BITRIX_OFFER_ID_ALT &&
                        updatedPart.bitrixId === originalItem.BITRIX_OFFER_ID_ALT.toString();

                    if ((isUsingAltId || isUsingOriginalAltId) && originalItem.nameNomenklAlt) {
                        updatedPart.name = originalItem.nameNomenklAlt ?? updatedPart.name;
                        updatedPart.price = originalItem.priceAltNomenkl ?? updatedPart.price;
                    } else if (!isUsingAltId && !isUsingOriginalAltId) {
                        updatedPart.name = originalItem.nameNomenkl ?? updatedPart.name;
                        updatedPart.price = originalItem.priceNomenkl ?? updatedPart.price;
                    }
                }

                return updatedPart;
            }),
        })),
    }));
};
